// Client/src/components/QRCodeDisplay.tsx

import { useEffect, useMemo, useState } from 'react';
import { Clipboard, Download, X } from 'lucide-react';
import Portal from './Portal';
import { useUiComponent } from '../utils/uiComponentTracker';

interface QRCodeDisplayProps {
  qrCodeUrl: string;
  password?: string;
  loginUrl?: string;
  onClose: () => void;
  minimal?: boolean;
  autoCloseSeconds?: number;
}

export default function QRCodeDisplay({
  qrCodeUrl,
  password,
  loginUrl,
  onClose,
  minimal = false,
  autoCloseSeconds,
}: QRCodeDisplayProps) {
  useUiComponent('QRCodeDisplayModal', 'modal');
  const resolvedAutoCloseSeconds = useMemo(
    () => autoCloseSeconds ?? (minimal ? 60 : 0),
    [autoCloseSeconds, minimal]
  );
  const [autoCloseProgress, setAutoCloseProgress] = useState(1);
  const [remainingSeconds, setRemainingSeconds] = useState(resolvedAutoCloseSeconds);

  useEffect(() => {
    if (resolvedAutoCloseSeconds <= 0) {
      return;
    }

    setAutoCloseProgress(1);
    setRemainingSeconds(resolvedAutoCloseSeconds);

    const durationMs = resolvedAutoCloseSeconds * 1000;
    const startedAt = performance.now();
    let frameId = 0;

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const ratio = Math.max(1 - elapsed / durationMs, 0);
      setAutoCloseProgress(ratio);
      setRemainingSeconds(Math.max(0, Math.ceil((durationMs - elapsed) / 1000)));

      if (ratio > 0) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    const timeoutId = window.setTimeout(onClose, durationMs);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [onClose, qrCodeUrl, resolvedAutoCloseSeconds]);

  const handleCopy = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      alert('In Zwischenablage kopiert!');
    } catch (error) {
      console.error('Fehler beim Kopieren in die Zwischenablage:', error);
      alert('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = 'qr-code.png';
    link.click();
    link.remove();
  };

  return (
    <Portal label="QRCodeDisplayModal">
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 modal-overlay"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`relative w-full bg-white shadow-2xl ${
            minimal
              ? 'max-w-[24rem] overflow-hidden rounded-2xl p-5 pt-14'
              : 'max-w-md max-h-[calc(var(--app-window-height)*0.85)] overflow-auto rounded-md'
          }`}
        >
          {minimal ? (
            <>
              {resolvedAutoCloseSeconds > 0 && (
                <div className="absolute left-0 top-0 h-1 w-full bg-slate-100">
                  <div
                    className="h-full bg-emerald-500 transition-[width] duration-100"
                    style={{ width: `${autoCloseProgress * 100}%` }}
                  />
                </div>
              )}
              <div className="absolute left-5 top-4 text-sm font-semibold text-slate-500">
                {remainingSeconds > 0 ? `${remainingSeconds}s` : ''}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white shadow-md transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                aria-label="Modal schließen"
                title="Schließen"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </>
          ) : (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                QR-Code
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                aria-label="Modal schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className={minimal ? 'flex justify-center' : 'p-4 space-y-4'}>
            <div className="flex justify-center">
              <img
                src={qrCodeUrl}
                alt="QR Code"
                className={minimal ? 'h-80 w-80 max-w-full object-contain' : 'h-56 w-56 object-contain'}
              />
            </div>
          </div>

          {!minimal && (
            <>
              {loginUrl && (
                <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mx-4 mb-4 space-y-1">
                  <label className="text-sm text-gray-600 block">Login-URL:</label>
                  <div className="flex items-center space-x-2">
                    <span className="flex-1 text-sm text-gray-800 break-words">
                      {loginUrl}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(loginUrl)}
                      className="inline-flex items-center px-2 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <Clipboard className="h-4 w-4 mr-1" />
                      Kopieren
                    </button>
                  </div>
                </div>
              )}

              {password && (
                <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mx-4 mb-4 space-y-1">
                  <label className="text-sm text-gray-600 block">
                    Neues Passwort:
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="flex-1 text-sm text-red-700 font-semibold break-words">
                      {password}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(password)}
                      className="inline-flex items-center px-2 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <Clipboard className="h-4 w-4 mr-1" />
                      Kopieren
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row items-center justify-end gap-2 px-4 py-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-md"
                >
                  <Download className="h-4 w-4 mr-2" />
                  QR-Code herunterladen
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gray-300 text-gray-700 hover:bg-gray-400 rounded-md"
                >
                  Schließen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Portal>
  );
}
